// Global Instructions Rule Applied!
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Modal, Space, Radio, Input, Upload } from 'antd'
import type { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLink, faUpload } from '@fortawesome/free-solid-svg-icons'
import Button from '../../Button.jsx'
import { FILE_TYPE, URL_TYPE } from '../../../../state_manager/constants/files.ts'

const { Dragger } = Upload

interface UploadData {
  type: typeof FILE_TYPE | typeof URL_TYPE
  files?: File[]
  url?: string
  fileName?: string
}

interface UploadModalProps {
  visible: boolean
  onCancel: () => void
  onUpload: (uploadData: UploadData) => Promise<void> | void
  loading?: boolean
}

/**
 * Upload Modal Component
 * Allows users to upload files or add URLs
 */
const UploadModal = ({ visible, onCancel, onUpload, loading = false }: UploadModalProps) => {
  const [uploadType, setUploadType] = useState<typeof FILE_TYPE | typeof URL_TYPE>(FILE_TYPE)
  // What antd's Upload hands over: an RcFile is a File with the uid antd stamps on it.
  const [selectedFiles, setSelectedFiles] = useState<RcFile[]>([])
  const [urlValue, setUrlValue] = useState('')
  const [fileName, setFileName] = useState('')

  // Reset state when modal opens
  const resetState = useCallback(() => {
    setUploadType(FILE_TYPE)
    setSelectedFiles([])
    setUrlValue('')
    setFileName('')
  }, [])

  useEffect(() => {
    if (visible) {
      resetState()
    }
  }, [visible, resetState])

  const handleCancel = useCallback(() => {
    resetState()
    onCancel?.()
  }, [resetState, onCancel])

  const handleUpload = useCallback(async () => {
    if (uploadType === FILE_TYPE && selectedFiles.length > 0) {
      await onUpload?.({ type: FILE_TYPE, files: selectedFiles })
      resetState()
    } else if (uploadType === URL_TYPE && urlValue.trim() && fileName.trim()) {
      await onUpload?.({ type: URL_TYPE, url: urlValue.trim(), fileName: fileName.trim() })
      resetState()
    }
  }, [uploadType, selectedFiles, urlValue, fileName, onUpload, resetState])

  const handleFileChange = useCallback((info: UploadChangeParam<UploadFile<File>>) => {
    const { fileList } = info
    const files = fileList
      .map((item) => item.originFileObj || item)
      .filter((item): item is RcFile => item instanceof File)
    setSelectedFiles(files)
  }, [])

  const isUploadDisabled = useMemo(() => {
    if (uploadType === FILE_TYPE) {
      return selectedFiles.length === 0
    }
    return !urlValue.trim() || !fileName.trim()
  }, [uploadType, selectedFiles.length, urlValue, fileName])

  return (
    <Modal
      title='Upload File or URL'
      open={visible}
      onCancel={handleCancel}
      closable={false}
      maskClosable={false}
      footer={
        <Space>
          <Button variant='cancel' onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant='save' onClick={handleUpload} disabled={isUploadDisabled || loading} loading={loading}>
            {uploadType === URL_TYPE ? 'Save' : 'Upload'}
          </Button>
        </Space>
      }
      width={600}
      destroyOnClose={true}
    >
      <div className='space-y-4'>
        {/* Upload Type Radio Buttons */}
        <div>
          <label className='block text-sm font-medium mb-2'>Upload Type</label>
          <Radio.Group value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
            <Radio value={FILE_TYPE}>File</Radio>
            <Radio value={URL_TYPE}>URL</Radio>
          </Radio.Group>
        </div>

        {/* File Upload Dropzone */}
        {uploadType === FILE_TYPE && (
          <div>
            <label className='block text-sm font-medium mb-2'>Select Files</label>
            <Dragger
              name='file'
              multiple={true}
              beforeUpload={() => false} // Prevent auto-upload
              onChange={handleFileChange}
              fileList={selectedFiles.map((file, index) => ({
                uid: `${file.name}-${index}`,
                name: file.name,
                size: file.size,
                status: 'done' as const,
                originFileObj: file
              }))}
            >
              <p className='ant-upload-drag-icon'>
                <FontAwesomeIcon icon={faUpload} className='text-4xl text-gray-400' />
              </p>
              <p className='ant-upload-text'>Click or drag files to this area to upload</p>
              <p className='ant-upload-hint'>Support for multiple file upload</p>
            </Dragger>
          </div>
        )}

        {/* URL Input */}
        {uploadType === URL_TYPE && (
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium mb-2'>
                <span className='text-red-500'>*</span> File Name
              </label>
              <Input
                placeholder='Enter file name'
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                onPressEnter={handleUpload}
                required
                status={!fileName.trim() ? 'error' : undefined}
              />
            </div>
            <div>
              <label className='block text-sm font-medium mb-2'>
                <span className='text-red-500'>*</span> Enter URL
              </label>
              <Input
                placeholder='https://example.com/file.pdf'
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                onPressEnter={handleUpload}
                prefix={<FontAwesomeIcon icon={faLink} className='text-gray-400' />}
                required
                status={!urlValue.trim() ? 'error' : undefined}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
export default UploadModal
